/**
 * Der Abgleich: Was hat dieser Kunde von unserem Leistungsspektrum, was bewusst nicht —
 * und was wurde nie besprochen?
 *
 * Die dritte Kategorie ist der Grund für diesen Dialog. Sie ist nicht „unbearbeitet",
 * sondern die Lücke: die Leistungen, über die mit diesem Kunden noch nie jemand
 * gesprochen hat. Deshalb steht sie oben in der Zusammenfassung und lässt sich als
 * einzige Ansicht filtern — wer den Abgleich öffnet, will meistens genau die sehen.
 *
 * Kein Speichern-Knopf: Jeder Klick schreibt sofort. Bei 42 Zeilen wäre ein Formular
 * mit Sammelspeicherung eine Einladung, auf halbem Weg abzubrechen und alles zu
 * verlieren.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconEyeOff, IconTargetArrow, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../../lib/api';
import {
  OFFERING_STATUS_LABELS,
  type CustomerOfferingStatus,
  type OfferingMatch,
} from '../../lib/api/domains/customerCare';

type Filter = 'ALL' | 'GAP' | 'ACTIVE' | 'PROPOSED' | 'DECLINED';

const STATUS_COLOR: Record<CustomerOfferingStatus, string> = {
  ACTIVE: 'teal',
  PROPOSED: 'indigo',
  DECLINED: 'gray',
};

export function PortfolioMatchModal({
  customerId,
  customerName,
  opened,
  onClose,
}: {
  customerId: string;
  customerName: string;
  opened: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['customer-portfolio', customerId];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => api.getCustomerPortfolio(customerId),
    enabled: opened,
  });

  const [filter, setFilter] = useState<Filter>('GAP');

  const setStatus = useMutation({
    mutationFn: (vars: {
      offeringKey: string;
      status: CustomerOfferingStatus | null;
      note?: string | null;
    }) => api.setCustomerOffering(customerId, vars.offeringKey, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // Der Rückblick schlägt nur noch vor, was offen ist — die Auswahl ändert sich mit.
      queryClient.invalidateQueries({ queryKey: ['customer-report'] });
    },
    onError: (e: Error) =>
      notifications.show({ title: 'Nicht gespeichert', message: e.message, color: 'red' }),
  });

  const visible = useMemo(() => {
    const all = data?.offerings ?? [];
    if (filter === 'ALL') return all;
    if (filter === 'GAP') return all.filter((o) => o.status === null);
    return all.filter((o) => o.status === filter);
  }, [data, filter]);

  const grouped = useMemo(() => {
    const order = data?.categoryOrder ?? [];
    const byCategory = new Map<string, OfferingMatch[]>();
    for (const o of visible) {
      const list = byCategory.get(o.category) ?? [];
      list.push(o);
      byCategory.set(o.category, list);
    }
    return order
      .filter((c) => byCategory.has(c))
      .map((c) => ({ category: c, label: data?.categoryLabels[c] ?? c, items: byCategory.get(c)! }));
  }, [visible, data]);

  const s = data?.summary;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Leistungsabgleich — ${customerName}`}
      size="xl"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">
        {s && (
          <Alert color={s.unrated > 0 ? 'blue' : 'teal'} variant="light">
            <Text size="sm">
              {s.active} von {s.total} Leistungen laufen bereits, {s.proposed} sind vorgemerkt,{' '}
              {s.declined} passen nicht.{' '}
              {s.unrated > 0 ? (
                <>
                  <strong>{s.unrated} wurden mit diesem Kunden noch nie besprochen</strong> — das
                  ist die eigentliche Liste.
                </>
              ) : (
                'Jede Leistung ist bewertet.'
              )}
            </Text>
          </Alert>
        )}

        <SegmentedControl
          fullWidth
          value={filter}
          onChange={(value) => setFilter(value as Filter)}
          data={[
            { value: 'GAP', label: `Nie besprochen${s ? ` (${s.unrated})` : ''}` },
            { value: 'ACTIVE', label: `Läuft${s ? ` (${s.active})` : ''}` },
            { value: 'PROPOSED', label: `Anbieten${s ? ` (${s.proposed})` : ''}` },
            { value: 'DECLINED', label: `Passt nicht${s ? ` (${s.declined})` : ''}` },
            { value: 'ALL', label: 'Alle' },
          ]}
        />

        {isLoading && <Text size="sm" c="dimmed">Wird geladen…</Text>}

        {!isLoading && visible.length === 0 && (
          <Text size="sm" c="dimmed">
            In dieser Ansicht steht nichts.
          </Text>
        )}

        {grouped.map((group) => (
          <Box key={group.category}>
            <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={6}>
              {group.label}
            </Text>
            <Stack gap="xs">
              {group.items.map((offering) => (
                <OfferingRow
                  key={offering.key}
                  offering={offering}
                  pending={setStatus.isPending}
                  onSet={(status, note) =>
                    setStatus.mutate({ offeringKey: offering.key, status, note })
                  }
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Modal>
  );
}

function OfferingRow({
  offering,
  pending,
  onSet,
}: {
  offering: OfferingMatch;
  pending: boolean;
  onSet: (status: CustomerOfferingStatus | null, note?: string | null) => void;
}) {
  // Die Notiz ist beim Ablehnen die wichtigere Hälfte: „läuft über den Steuerberater"
  // erspart beim nächsten Abgleich dieselbe Frage. Deshalb klappt sie genau dann auf.
  const [note, setNote] = useState(offering.note ?? '');
  const [noteOpen, setNoteOpen] = useState(false);

  const set = (status: CustomerOfferingStatus | null) => {
    if (status === offering.status) {
      // Nochmal derselbe Knopf = Bewertung zurücknehmen. Die Leistung landet wieder
      // in „nie besprochen", statt dauerhaft falsch markiert zu bleiben.
      onSet(null, null);
      return;
    }
    onSet(status, note.trim() || null);
    if (status === 'DECLINED' && !note.trim()) setNoteOpen(true);
  };

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap={6}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="sm" fw={500}>
                {offering.name}
              </Text>
              {offering.customerVisible === false && (
                <Tooltip
                  label="Steht nicht in der Auslage im Kundenportal — gehört ins Gespräch"
                  withArrow
                >
                  <IconEyeOff size={13} style={{ color: 'var(--mantine-color-gray-5)' }} />
                </Tooltip>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {offering.pitch}
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
              {offering.effortHint} · {offering.valueHint}
            </Text>
          </Box>
          <Group gap={4} wrap="nowrap">
            <StatusButton
              status="ACTIVE"
              current={offering.status}
              icon={<IconCheck size={12} />}
              disabled={pending}
              onClick={() => set('ACTIVE')}
            />
            <StatusButton
              status="PROPOSED"
              current={offering.status}
              icon={<IconTargetArrow size={12} />}
              disabled={pending}
              onClick={() => set('PROPOSED')}
            />
            <StatusButton
              status="DECLINED"
              current={offering.status}
              icon={<IconX size={12} />}
              disabled={pending}
              onClick={() => set('DECLINED')}
            />
          </Group>
        </Group>

        {(offering.note || noteOpen) && (
          <Group gap="xs" wrap="nowrap">
            <TextInput
              size="xs"
              style={{ flex: 1 }}
              placeholder={'Warum — z. B. „läuft über den Steuerberater“'}
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
            />
            <Button
              size="compact-xs"
              variant="light"
              disabled={pending || !offering.status}
              onClick={() => {
                onSet(offering.status, note.trim() || null);
                setNoteOpen(false);
              }}
            >
              Notiz speichern
            </Button>
          </Group>
        )}

        {offering.setByName && offering.status && (
          <Text size="xs" c="dimmed">
            {OFFERING_STATUS_LABELS[offering.status]} · gesetzt von {offering.setByName}
            {offering.updatedAt
              ? ` am ${new Date(offering.updatedAt).toLocaleDateString('de-DE')}`
              : ''}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function StatusButton({
  status,
  current,
  icon,
  disabled,
  onClick,
}: {
  status: CustomerOfferingStatus;
  current: CustomerOfferingStatus | null;
  icon: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  const active = current === status;
  return (
    <Tooltip
      label={active ? `${OFFERING_STATUS_LABELS[status]} — nochmal klicken hebt es auf` : OFFERING_STATUS_LABELS[status]}
      withArrow
    >
      <Button
        size="compact-xs"
        variant={active ? 'filled' : 'subtle'}
        color={active ? STATUS_COLOR[status] : 'gray'}
        leftSection={icon}
        disabled={disabled}
        onClick={onClick}
      >
        {OFFERING_STATUS_LABELS[status]}
      </Button>
    </Tooltip>
  );
}
